/**
 * Admin - Edit User Page
 *
 * Full user management with tabs for profile, voice samples, topics, and custom prompts
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiRequest } from '../../platform/api';
import { dedupeRequest } from '../../lib/request-cache';
import {
  ArrowLeft,
  User,
  Mic,
  Tag,
  FileText,
  Save,
  Plus,
  Trash2,
  Code,
  UserPlus,
  RotateCcw,
  CreditCard,
  CalendarPlus,
} from 'lucide-react';

interface VoiceSample {
  id: string;
  content: string;
  sample_type: string;
  created_at: string;
}

interface CustomPrompt {
  id: string;
  name: string;
  type: string;
  is_active: boolean;
  model: string;
  created_at: string;
}

interface Subscription {
  user_id: string;
  plan_id: string;
  status: string;
  trial_ends_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  created_at: string;
  updated_at: string;
}

interface BillingPlan {
  id: string;
  name: string;
  price_cents: number;
  billing_interval?: string;
}

interface UserData {
  profile: {
    user_id: string;
    voice_profile: any;
    tone_preference: string | null;
    narrative_style: string | null;
    topics_include: string[] | null;
    topics_exclude: string[] | null;
    onboarding_completed: boolean;
    is_admin: boolean;
    created_at: string;
  };
  logto: {
    primaryEmail: string;
    name: string;
  } | null;
  voice_samples: VoiceSample[];
  custom_prompts: CustomPrompt[];
  subscription: Subscription | null;
  current_plan: BillingPlan | null;
  stats: {
    sources_count: number;
    knowledge_items_count: number;
    posts_count: number;
    voice_samples_count: number;
  };
}

type Tab = 'profile' | 'voice' | 'topics' | 'prompts' | 'subscription';

export function AdminUserEditPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<Tab>('profile');
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Topics state
  const [topicsInclude, setTopicsInclude] = useState<string[]>([]);
  const [topicsExclude, setTopicsExclude] = useState<string[]>([]);
  const [newTopic, setNewTopic] = useState('');

  // Subscription state
  const [allPlans, setAllPlans] = useState<BillingPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [trialDays, setTrialDays] = useState(14);
  const [assigningPlan, setAssigningPlan] = useState(false);
  const [extendDays, setExtendDays] = useState(14);
  const [extending, setExtending] = useState(false);

  // Refs to prevent infinite loops
  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);

  const loadUserData = useCallback(async () => {
    if (!userId || isLoadingRef.current) return;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const [result, plansResult] = await Promise.all([
        dedupeRequest(`admin-user-${userId}`, () =>
          apiRequest<{ data: UserData }>(`/api/admin/users/${userId}`, {
            method: 'GET',
          })
        ),
        apiRequest<{ data: BillingPlan[] }>('/api/admin/pricing/plans', {
          method: 'GET',
        }),
      ]);
      setUserData(result.data);
      setTopicsInclude(result.data.profile.topics_include || []);
      setTopicsExclude(result.data.profile.topics_exclude || []);
      if (plansResult.data) {
        setAllPlans(plansResult.data);
      }
      hasLoadedRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [userId]);

  useEffect(() => {
    if (userId && !hasLoadedRef.current) {
      loadUserData();
    }
  }, [userId, loadUserData]);

  const handleSaveTopics = async () => {
    try {
      setSaving(true);
      await apiRequest(`/api/admin/users/${userId}/topics`, {
        method: 'PATCH',
        body: {
          topics_include: topicsInclude,
          topics_exclude: topicsExclude,
        },
      });
      await loadUserData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save topics');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVoiceSample = async (sampleId: string) => {
    if (!confirm('Are you sure you want to delete this voice sample?')) return;

    try {
      await apiRequest(`/api/admin/users/${userId}/voice-samples/${sampleId}`, {
        method: 'DELETE',
      });
      await loadUserData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete voice sample');
    }
  };

  const handleResetProfile = async () => {
    if (!confirm('Reset this user? This deletes ALL data (posts, sources, knowledge, voice samples) and resets onboarding. This cannot be undone.')) return;

    try {
      setResetting(true);
      await apiRequest(`/api/admin/users/${userId}/reset`, { method: 'POST' });
      hasLoadedRef.current = false;
      await loadUserData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset user');
    } finally {
      setResetting(false);
    }
  };

  const addTopicToInclude = () => {
    if (newTopic && !topicsInclude.includes(newTopic)) {
      setTopicsInclude([...topicsInclude, newTopic]);
      setTopicsExclude(topicsExclude.filter((t) => t !== newTopic));
      setNewTopic('');
    }
  };

  const addTopicToExclude = () => {
    if (newTopic && !topicsExclude.includes(newTopic)) {
      setTopicsExclude([...topicsExclude, newTopic]);
      setTopicsInclude(topicsInclude.filter((t) => t !== newTopic));
      setNewTopic('');
    }
  };

  const handleAssignPlan = async () => {
    if (!selectedPlanId) return;

    try {
      setAssigningPlan(true);
      const selectedPlan = allPlans.find((p) => p.id === selectedPlanId);
      const isPaid = selectedPlan && selectedPlan.price_cents > 0;

      await apiRequest(`/api/admin/users/${userId}/assign-plan`, {
        method: 'POST',
        body: {
          plan_id: selectedPlanId,
          ...(isPaid ? { trial_days: trialDays } : {}),
        },
      });

      setSelectedPlanId('');
      setTrialDays(14);
      hasLoadedRef.current = false;
      await loadUserData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign plan');
    } finally {
      setAssigningPlan(false);
    }
  };

  const handleExtendTrial = async () => {
    try {
      setExtending(true);
      await apiRequest(`/api/admin/users/${userId}/extend-trial`, {
        method: 'POST',
        body: { days: extendDays },
      });
      hasLoadedRef.current = false;
      await loadUserData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to extend trial');
    } finally {
      setExtending(false);
    }
  };

  const tabs = [
    { id: 'profile' as Tab, label: 'Profile', icon: User },
    { id: 'voice' as Tab, label: 'Voice Samples', icon: Mic },
    { id: 'topics' as Tab, label: 'Topics', icon: Tag },
    { id: 'prompts' as Tab, label: 'Custom Prompts', icon: FileText },
    { id: 'subscription' as Tab, label: 'Subscription', icon: CreditCard },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-mazo-600"></div>
          <p className="mt-2 text-sm text-gray-500">Loading user...</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">User not found</p>
          <button
            onClick={() => navigate('/admin/users')}
            className="mt-4 text-mazo-600 hover:text-mazo-700"
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/users')}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">
                {userData.logto?.name || 'User'}
              </h1>
              <p className="mt-1 text-sm text-gray-500">{userData.logto?.primaryEmail}</p>
            </div>
            <button
              onClick={handleResetProfile}
              disabled={resetting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors text-sm font-medium"
            >
              <RotateCcw className={`w-4 h-4 ${resetting ? 'animate-spin' : ''}`} />
              {resetting ? 'Resetting...' : 'Reset Profile'}
            </button>
            <button
              onClick={() =>
                navigate(`/onboarding?admin=true&mode=onboard&userId=${userId}`)
              }
              className="px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors text-sm font-medium"
            >
              Onboard User
            </button>
          </div>

          {/* Tabs */}
          <div className="mt-6 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-mazo-500 text-mazo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">User Information</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">User ID</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">{userData.profile.user_id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Role</dt>
                  <dd className="mt-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        userData.profile.is_admin
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-mazo-100 text-mazo-800'
                      }`}
                    >
                      {userData.profile.is_admin ? 'Admin' : 'User'}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Onboarding Status</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {userData.profile.onboarding_completed ? 'Completed' : 'In Progress'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(userData.profile.created_at).toLocaleDateString()}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Statistics</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Sources</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">
                    {userData.stats.sources_count}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Knowledge Items</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">
                    {userData.stats.knowledge_items_count}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Posts</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">
                    {userData.stats.posts_count}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Voice Samples</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">
                    {userData.stats.voice_samples_count}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Simulate Onboarding Action */}
            <div className="bg-gradient-to-r from-mazo-50 to-purple-50 rounded-lg border-2 border-mazo-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <UserPlus className="w-5 h-5 text-mazo-600" />
                    <h3 className="text-lg font-medium text-gray-900">Simulate Onboarding</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Walk through the onboarding process for this user. You can add voice samples,
                    configure topics, and generate their voice profile.
                  </p>
                  <button
                    onClick={() =>
                      navigate(`/onboarding?admin=true&mode=onboard&userId=${userId}`)
                    }
                    className="inline-flex items-center gap-2 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors font-medium"
                  >
                    <UserPlus className="w-4 h-4" />
                    Start Onboarding Simulation
                  </button>
                </div>
              </div>
            </div>

            {userData.profile.voice_profile && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Voice Profile</h3>
                  <Code className="w-5 h-5 text-gray-400" />
                </div>
                <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-xs">
                  {JSON.stringify(userData.profile.voice_profile, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Voice Samples Tab */}
        {activeTab === 'voice' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Voice Samples</h3>
                <button className="inline-flex items-center gap-2 px-3 py-1.5 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors text-sm">
                  <Plus className="w-4 h-4" />
                  Add Sample
                </button>
              </div>

              {userData.voice_samples.length === 0 ? (
                <div className="text-center py-8">
                  <Mic className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No voice samples</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Add voice samples to generate the user's voice profile
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {userData.voice_samples.map((sample) => (
                    <div
                      key={sample.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-mazo-100 text-mazo-800">
                          {sample.sample_type}
                        </span>
                        <button
                          onClick={() => handleDeleteVoiceSample(sample.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{sample.content}</p>
                      <p className="mt-2 text-xs text-gray-500">
                        {new Date(sample.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Topics Tab */}
        {activeTab === 'topics' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Topic Preferences</h3>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add New Topic
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTopic}
                    onChange={(e) => setNewTopic(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTopicToInclude()}
                    placeholder="Enter topic name..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-transparent"
                  />
                  <button
                    onClick={addTopicToInclude}
                    className="px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors"
                  >
                    Include
                  </button>
                  <button
                    onClick={addTopicToExclude}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Exclude
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Include */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Include Topics</h4>
                  <div className="flex flex-wrap gap-2">
                    {topicsInclude.map((topic) => (
                      <span
                        key={topic}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                      >
                        {topic}
                        <button
                          onClick={() => setTopicsInclude(topicsInclude.filter((t) => t !== topic))}
                          className="hover:text-green-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    {topicsInclude.length === 0 && (
                      <p className="text-sm text-gray-500">No topics to include</p>
                    )}
                  </div>
                </div>

                {/* Exclude */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Exclude Topics</h4>
                  <div className="flex flex-wrap gap-2">
                    {topicsExclude.map((topic) => (
                      <span
                        key={topic}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm"
                      >
                        {topic}
                        <button
                          onClick={() => setTopicsExclude(topicsExclude.filter((t) => t !== topic))}
                          className="hover:text-red-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    {topicsExclude.length === 0 && (
                      <p className="text-sm text-gray-500">No topics to exclude</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={handleSaveTopics}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Topics'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Custom Prompts Tab */}
        {activeTab === 'prompts' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Custom Prompts</h3>
                <button className="inline-flex items-center gap-2 px-3 py-1.5 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors text-sm">
                  <Plus className="w-4 h-4" />
                  Add Prompt
                </button>
              </div>

              {userData.custom_prompts.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No custom prompts</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Create custom prompts to override system defaults for this user
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {userData.custom_prompts.map((prompt) => (
                    <div
                      key={prompt.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-gray-900">{prompt.name}</h4>
                            {prompt.is_active && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Active
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                              {prompt.type}
                            </span>
                            <span>{prompt.model}</span>
                            <span>{new Date(prompt.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <button className="text-mazo-600 hover:text-mazo-700 text-sm font-medium">
                          Edit
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div className="space-y-6">
            {/* Current Plan */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Current Plan</h3>

              {userData.subscription ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="text-base font-semibold text-gray-900">
                      {userData.current_plan?.name || userData.subscription.plan_id}
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        userData.subscription.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : userData.subscription.status === 'trialing'
                          ? (() => {
                              const isExpired =
                                userData.subscription.trial_ends_at &&
                                new Date(userData.subscription.trial_ends_at) < new Date();
                              return isExpired
                                ? 'bg-red-100 text-red-800'
                                : 'bg-blue-100 text-blue-800';
                            })()
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {userData.subscription.status === 'trialing' &&
                      userData.subscription.trial_ends_at &&
                      new Date(userData.subscription.trial_ends_at) < new Date()
                        ? 'Trial Expired'
                        : userData.subscription.status}
                    </span>
                  </div>

                  {userData.subscription.status === 'trialing' &&
                    userData.subscription.trial_ends_at && (
                      <div className="space-y-2">
                        {(() => {
                          const trialEnd = new Date(userData.subscription.trial_ends_at);
                          const now = new Date();
                          const isExpired = trialEnd < now;
                          const daysRemaining = Math.ceil(
                            (trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
                          );

                          return isExpired ? (
                            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                              <span className="text-sm text-red-700">
                                Trial expired on {trialEnd.toLocaleDateString()}. User is now on free
                                plan limits.
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <span className="text-sm text-blue-700">
                                Trial ends on {trialEnd.toLocaleDateString()} ({daysRemaining}{' '}
                                {daysRemaining === 1 ? 'day' : 'days'} remaining)
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                  <dl className="grid grid-cols-2 gap-4 pt-2">
                    {userData.current_plan && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Price</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {userData.current_plan.price_cents === 0
                            ? 'Free'
                            : `€${(userData.current_plan.price_cents / 100).toFixed(0)}/${userData.current_plan.billing_interval || 'month'}`}
                        </dd>
                      </div>
                    )}
                    {userData.subscription.stripe_subscription_id && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Stripe Subscription</dt>
                        <dd className="mt-1 text-sm text-gray-900 font-mono text-xs">
                          {userData.subscription.stripe_subscription_id}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              ) : (
                <div className="text-center py-4">
                  <CreditCard className="mx-auto h-10 w-10 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">
                    No subscription found. User is on the default free plan.
                  </p>
                </div>
              )}
            </div>

            {/* Extend Trial — only show when status is trialing */}
            {userData.subscription?.status === 'trialing' && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <CalendarPlus className="w-5 h-5 text-blue-600" />
                  Extend Trial
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Days
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={extendDays}
                      onChange={(e) => setExtendDays(Number(e.target.value))}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Extends the trial end date by this many days from today.
                    </p>
                  </div>

                  <button
                    onClick={handleExtendTrial}
                    disabled={extending}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    <CalendarPlus className="w-4 h-4" />
                    {extending ? 'Extending...' : 'Extend Trial'}
                  </button>
                </div>
              </div>
            )}

            {/* Assign Plan */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Assign Plan</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                  <select
                    value={selectedPlanId}
                    onChange={(e) => setSelectedPlanId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-transparent"
                  >
                    <option value="">Select a plan...</option>
                    {allPlans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name}{' '}
                        {plan.price_cents === 0
                          ? '(Free)'
                          : `(€${(plan.price_cents / 100).toFixed(0)}/${plan.billing_interval || 'month'})`}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedPlanId &&
                  allPlans.find((p) => p.id === selectedPlanId)?.price_cents! > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Trial Days
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={trialDays}
                        onChange={(e) => setTrialDays(Number(e.target.value))}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        User will have full access to this plan during the trial period. After the
                        trial expires, they will fall back to free plan limits until they purchase.
                      </p>
                    </div>
                  )}

                <button
                  onClick={handleAssignPlan}
                  disabled={!selectedPlanId || assigningPlan}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors disabled:opacity-50"
                >
                  <CreditCard className="w-4 h-4" />
                  {assigningPlan ? 'Assigning...' : 'Assign Plan'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
